import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { FaArrowLeft, FaCreditCard, FaWallet, FaMobileAlt, FaMoneyBillWave } from 'react-icons/fa';
import { clearSelection, confirmOrder } from '../../redux/slices/tiffinSlice';
import { getColors } from '../../styles/colors';
import OrderSummary from '../order/OrderSummary';
import PaymentSummary from '../payment/PaymentSummary';

const Payment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderItems, selectedDate, selectedTime } = useSelector((state) => state.tiffin);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  const hasItems = orderItems.length > 0;

  const handleConfirmOrder = () => {
    dispatch(confirmOrder());
    navigate('/order-success');
  };

  const handleClear = () => {
    dispatch(clearSelection());
    navigate('/meal-select');
  };

  if (!hasItems || !selectedDate || !selectedTime) {
    return (
      <motion.div 
        className="py-4 px-4 max-w-4xl mx-auto"
        style={{ backgroundColor: colors.background }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-4 mb-6">
          <Link to="/time-select" className="text-2xl" style={{ color: colors.text }}>
            <FaArrowLeft />
          </Link>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>💳 Payment</h1>
        </div>
        <div 
          className="rounded-2xl p-12 text-center"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <p className="text-4xl mb-4">💳</p>
          <p className="text-lg" style={{ color: colors.textSecondary }}>Please complete all selections first</p>
          <Link to="/meal-select">
            <button 
              className="mt-4 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
              style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
            >
              Start Over
            </button>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="py-4 px-4 max-w-6xl mx-auto"
      style={{ backgroundColor: colors.background }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Link to="/time-select" className="text-2xl" style={{ color: colors.text }}>
          <FaArrowLeft />
        </Link>
        <h1 className="text-2xl font-bold" style={{ color: colors.text }}>💳 Payment</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <OrderSummary />
          
          <div 
            className="rounded-2xl p-6"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <h3 className="font-bold mb-4" style={{ color: colors.text }}>💳 Select Payment Method</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-3 rounded-xl border-2" style={{ borderColor: colors.primary, backgroundColor: colors.primary + '15' }}>
                <FaCreditCard style={{ color: colors.primary }} />
                <div>
                  <p className="font-semibold" style={{ color: colors.text }}>Credit/Debit Card</p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>Visa, Mastercard, RuPay</p>
                </div>
                <span className="ml-auto" style={{ color: colors.primary }}>✓</span>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-xl border" style={{ borderColor: colors.border }}>
                <FaWallet style={{ color: colors.textSecondary }} />
                <div>
                  <p className="font-semibold" style={{ color: colors.text }}>UPI</p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>Google Pay, PhonePe, Paytm</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-xl border" style={{ borderColor: colors.border }}>
                <FaMobileAlt style={{ color: colors.textSecondary }} />
                <div>
                  <p className="font-semibold" style={{ color: colors.text }}>Net Banking</p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>All major banks</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-xl border" style={{ borderColor: colors.border }}>
                <FaMoneyBillWave style={{ color: colors.textSecondary }} />
                <div>
                  <p className="font-semibold" style={{ color: colors.text }}>Cash on Delivery</p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>Pay when you receive</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <PaymentSummary />
          
          <div className="space-y-3">
            <button
              className="w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
              onClick={handleConfirmOrder}
            >
              💳 Confirm Order
            </button>
            <button
              className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ 
                backgroundColor: colors.border,
                color: colors.text
              }}
              onClick={handleClear}
            >
              🔄 Clear Selection
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Payment;